import React, { useState, useEffect } from "react";
import { DatePicker, Select, message, Button, Input } from "antd";
import { putRequest, getRequest, postRequest } from "../../service";
import { getEmployeeId } from "../../utils/localStorage";
import dayjs from "dayjs";


const LeaveRequestModal = ({ onClose, onSuccess, initialData, readOnly }) => {
  const [employeeList, setEmployeeList] = useState();
  const [formData, setFormData] = useState({
    leaveType: null,
    leaveDuration: "full",
    halfLeaveDuration: null,
    startDate: null,
    endDate: null,
    leaveDays: 0,
    reason: "",
    files: [],
    status: "pending", // default to pending
    note: "",
  });
  const [fileList, setFileList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const userID = getEmployeeId();
  
  // Fetch employee data
  useEffect(() => {
    const fetchEmployeeData = async () => {
      try {
        if (userID && initialData?.employeeId) {
          const response = await getRequest(`/api/employees/${initialData.employeeId}`);
          setEmployeeData(response);
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      }
    };
    const fetchEmployeeList = async () => {
      try {
        if (initialData?.employeeId) {
          const response = await getRequest(`/api/companies/teammembers?employeeId=${initialData.employeeId}`);
          if (response?.employee) {
            setEmployeeList(response);
          }
        }
      } catch (error) {
        console.error('Error fetching employee data:', error);
      } finally {
        // setLoading(false);
      }
    }
    fetchEmployeeData();
    fetchEmployeeList();
  }, [initialData?.employeeId]);
  
  // Populate form for edit mode
  useEffect(() => {
    if (initialData) {
      setFormData({
        leaveType: initialData.leaveType || null,
        leaveTypeName: initialData.leaveTypeName || initialData.leaveType || null,
        leaveDuration: initialData.leaveDuration || "full",
        halfLeaveDuration: initialData.halfLeaveDuration || null,
        startDate: initialData.startDate ? dayjs(initialData.startDate) : null,
        endDate: initialData.endDate ? dayjs(initialData.endDate) : null,
        leaveDays: initialData.leaveDays || 0,
        reason: initialData.reason || "",
        files: initialData.files || [],
        status: initialData.status || "pending",
        note: initialData.note || "",
      });
      setFileList((initialData.files || []).map(f => ({ name: f, uid: f })));
    }
  }, [initialData]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Special case for dates or duration: recalculate leave days
    if (field === "startDate") {
      calculateLeaveDays(value, formData.endDate, formData.leaveDuration);
    }
    if (field === "endDate") {
      calculateLeaveDays(formData.startDate, value, formData.leaveDuration);
    }
    if (field === "leaveDuration") {
      calculateLeaveDays(formData.startDate, formData.endDate, value);
    }
  };

  const calculateLeaveDays = (start, end, duration = formData.leaveDuration) => {
    if (start && end) {
      if (duration === 'half') {
        setFormData(prev => ({ ...prev, leaveDays: 0.5 }));
      } else if (start.isSame && start.isSame(end, 'day')) {
        setFormData(prev => ({ ...prev, leaveDays: 1 }));
      } else if (start.diff && end.diff) {
        const days = end.diff(start, 'day') + 1;
        setFormData(prev => ({ ...prev, leaveDays: days }));
      }
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      let leaveDays = formData.leaveDays;
      if (formData.leaveDuration === 'half') {
        leaveDays = 0.5;
      } else if (
        formData.startDate &&
        formData.endDate &&
        formData.startDate.isSame &&
        formData.startDate.isSame(formData.endDate, 'day')
      ) {
        leaveDays = 1;
      } else if (
        formData.startDate &&
        formData.endDate &&
        formData.endDate.diff &&
        formData.startDate.diff
      ) {
        leaveDays = formData.endDate.diff(formData.startDate, 'day') + 1;
      }
      const payload = {
        ...formData,
        startDate: formData.startDate ? formData.startDate.format ? formData.startDate.format("YYYY-MM-DD") : formData.startDate : null,
        endDate: formData.endDate ? formData.endDate.format ? formData.endDate.format("YYYY-MM-DD") : formData.endDate : null,
        files: formData.files,
        leaveDays,
        status: formData.status, // use selected status
        note: formData.note,
        approvedBy: userID,
      };
      let response;
      if (initialData && initialData._id) {
        response = await putRequest(`/api/leaveRequest?id=${initialData._id}`, payload);
      } 
      if (response.message === "Leave request updated successfully" ) {
        const managementIds = (employeeList?.managementChain || []).map(m => m?._id).filter(Boolean);
            const uniqueReceiverIds = Array.from(new Set([initialData.employeeId, ...managementIds].filter(Boolean)));
            const res = await postRequest('/api/notifications', {
              "title": `${employeeList.employee.name} Leave Request ${formData.status}`,
              "senderId": userID,
              "receiverIds": uniqueReceiverIds,
              "type": "LeaveRequest",
              "relatedTypeId": response.leaveRequest._id,
              "relatedDocumentModel": "LeaveRequest",
              "companyId": initialData?.companyId,
              "priority": "medium"
            });
        // Generate dummy data based on leave days
        const generateAttendanceData = () => {
          const attendanceData = [];
          const startDate = formData.startDate ? dayjs(formData.startDate) : null;
          const endDate = formData.endDate ? dayjs(formData.endDate) : null;
          
          if (startDate && endDate) {
            if (startDate.isSame(endDate, 'day')) {
              // Single day leave
              attendanceData.push({
                uid: employeeData?.employee?.machineCode || "",
                date: startDate.format("YYYY-MM-DD"),
                employeeId: employeeData?.employee?._id ,
                companyId: employeeData?.employee?.companyId ,
                checkIn: employeeData?.employee?.shift?.startTime || "-",
                checkOut: employeeData?.employee?.shift?.endTime || "-",
                overtime: "-",
                reqHour: employeeData?.employee?.shift?.workingHours || 9,
                workingHour: employeeData?.employee?.shift?.workingHours || 9,
                status: "Leave",
                approvedbyId: userID,
                perDaySelery: employeeData?.employee?.perday_salary || 0 ,
              });
            } else {
              // Multiple days leave - generate data for each day
              const daysDiff = endDate.diff(startDate, 'day') + 1;
              for (let i = 0; i < daysDiff; i++) {
                const currentDate = startDate.add(i, 'day');
                attendanceData.push({
                  uid: employeeData?.employee?.machineCode || "",
                  date: currentDate.format("YYYY-MM-DD"),
                  employeeId: employeeData?.employee?._id  ,
                  companyId: employeeData?.employee?.companyId ,
                  checkIn: employeeData?.employee?.shift?.startTime || "-",
                  checkOut: employeeData?.employee?.shift?.endTime || "-",
                  overtime: "-",
                  reqHour: employeeData?.employee?.shift?.workingHours || 9,
                  workingHour: employeeData?.employee?.shift?.workingHours || 9,
                  status:  "Leave",
                  approvedbyId: userID,
                  perDaySelery: employeeData?.employee?.perday_salary || 0 ,
                });
              }
            }
          }
          return attendanceData;
        };

        const attendanceData = generateAttendanceData();

        if (formData.status === "approved") {
          if (formData.leaveDuration === 'half') {
            try {
              const startDateStr = formData.startDate
                ? (formData.startDate.format ? formData.startDate.format("YYYY-MM-DD") : dayjs(formData.startDate).format("YYYY-MM-DD"))
                : null;
              const endDateStr = formData.endDate
                ? (formData.endDate.format ? formData.endDate.format("YYYY-MM-DD") : dayjs(formData.endDate).format("YYYY-MM-DD"))
                : null;

              if (employeeData?.employee?._id && startDateStr && endDateStr) {
                const logsResponse = await getRequest(`/api/myattendance?employeeId=${employeeData?.employee?._id}&startDate=${startDateStr}&endDate=${endDateStr}`);
                const attendanceLogs = Array.isArray(logsResponse?.attendanceLogs) ? logsResponse.attendanceLogs : [];

                if (attendanceLogs.length > 0) {
                  await Promise.all(
                    attendanceLogs.map((log) => {
                      const isFirstHalf = formData.halfLeaveDuration === 'firstHalf';
                      const isSecondHalf = formData.halfLeaveDuration === 'secondHalf';
                      const payload = { status: 'half' };
                      if (isFirstHalf) {
                        payload.checkIn = employeeData?.employee?.shift?.startTime || "-";
                      }
                      if (isSecondHalf) {
                        payload.checkOut = employeeData?.employee?.shift?.endTime || "-";
                      }
                      return putRequest(`/api/myattendance/${log._id}`, payload);
                    })
                  );
                }
              }
            } catch (error) {
              console.error('Error updating half-day attendance data:', error);
            }
          } else if (attendanceData.length > 0) {
            try {
              const attendanceResponse = await postRequest('/api/myattendance', attendanceData);
            } catch (error) {
              console.error('Error sending attendance data:', error);
            }
          }
        }

        message.success('Leave request submitted successfully!');
        onSuccess && onSuccess();
        onClose && onClose();
      } else {
        message.error(response.error || 'Failed to submit leave request');
      }
    } catch (err) {
      message.error('Failed to submit leave request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-white ">
      <form
        className="w-full"
        onSubmit={e => {
          e.preventDefault();
          if (!readOnly) handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Leave Type */}
          <label className="text-md text-gray-700 flex flex-col">
            Leave Type
            <Input
              className="w-full"
              placeholder="Enter Leave Type"
              value={formData.leaveTypeName || formData.leaveType}
              onChange={e => handleInputChange("leaveTypeName", e.target.value)}
              disabled={readOnly}
            />
          </label>
          {/* Leave Duration */}
          <label className="text-md text-gray-700 flex flex-col">
            Leave Duration
            <Select
              className="w-full"
              value={formData.leaveDuration}
              options={[
                { value: 'full', label: 'Full Day' },
                { value: 'half', label: 'Half Day' },
              ]}
              onChange={val => handleInputChange("leaveDuration", val)}
              disabled={readOnly}
            />
          </label>
          {formData.leaveDuration === 'half' && (
            <label className="text-md text-gray-700 flex flex-col">
              Half Day Selection
              <Input
                className="w-full"
                value={formData.halfLeaveDuration === 'secondHalf' ? 'Second Half' : (formData.halfLeaveDuration === 'firstHalf' ? 'First Half' : '')}
                placeholder="Half day selection"
                disabled
              />
            </label>
          )}
          {/* Leave From */}
          <label className="text-md text-gray-700 flex flex-col">
            Leave From
            <DatePicker
              onChange={date => handleInputChange("startDate", date)}
              className="w-full"
              value={formData.startDate}
              disabled={readOnly}
            />
          </label>
          {/* Leave To */}
          <label className="text-md text-gray-700 flex flex-col">
            Leave To
            <DatePicker
              onChange={date => handleInputChange("endDate", date)}
              className="w-full"
              value={formData.endDate}
              disabled={readOnly}
            />
          </label>
          {/* Leave Days */}
          <label className="text-md text-gray-700 flex flex-col">
            Leave Days
            <Input
              type="text"
              value={formData.leaveDays}
              className="block w-full border border-gray-400 p-1.5 rounded-md bg-gray-200"
              disabled
            />
          </label>
          {/* Status */}
          <label className="text-md text-gray-700 flex flex-col">
            Status
            <Select
              className="w-full"
              value={formData.status}
              options={[
                { value: "pending", label: "Pending" },
                { value: "approved", label: "Approved" },
                { value: "rejected", label: "Rejected" },
                { value: "cancelled", label: "Cancelled" },
              ]}
              onChange={val => handleInputChange("status", val)}
              disabled={readOnly}
            />
          </label>
        </div>
        {/* Note and Reason in two columns on md+ screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          {/* Note */}
          <label className="text-md text-gray-700 flex flex-col">
            Note
            <Input.TextArea
              className="block w-full border border-gray-400 p-1.5 rounded-md"
              rows={4}
              value={formData.note}
              onChange={e => handleInputChange("note", e.target.value)}
              disabled={readOnly}
            />
          </label>
          {/* Reason */}
          <label className="text-md text-gray-700 flex flex-col">
            Reason
            <Input.TextArea
              className="block w-full border border-gray-400 p-1.5 rounded-md"
              rows={4}
              value={formData.reason}
              onChange={e => handleInputChange("reason", e.target.value)}
              disabled={readOnly}
            />
          </label>
        </div>
        {/* File Links */}
        {Array.isArray(formData.files) && formData.files.length > 0 && (
          <div className="mt-4">
            <div className="font-semibold mb-1">Files:</div>
            <div className="flex flex-wrap gap-2">
              {formData.files.map((file, idx) => (
                <a
                  key={idx}
                  href={typeof file === 'string' ? file : file.url || file.name}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 underline hover:text-blue-800"
                >
                  View File {idx + 1}
                </a>
              ))}
            </div>
          </div>
        )}
        <div className="flex justify-end gap-4 mt-6">
          <Button
            type="default"
            onClick={onClose}
          >
            {readOnly ? 'Close' : 'Cancel'}
          </Button>
          {!readOnly && (
            <Button
              type="primary"
              className="bg-primary text-white hover:bg-primary"
              htmlType="submit"
              disabled={submitting}
            >
              {submitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          )}
        </div>
      </form>
    </div>
  );
};

export default LeaveRequestModal;
